import type { Integration } from '../../../core/domain/integration';
import type { ModelView } from '../../../core/domain/model';

/*
 * WHAT A FORWARD SENT FOR 1 MODEL.
 *
 * The Streaming tab of the model page (stage 1 of docs/model-streaming-tab-plan.md). The Forward
 * runner keeps no record of the values that it wrote, so the table of the Forward is the record.
 * The tab reads the last rows of the model back from that table with a read-only query.
 *
 * The query names the exact paths of the model. The primary key (symbol_path, ts) serves each
 * path. A LIKE on a prefix could not use the key in most collations, and an `_` in a model id
 * would match any character, so the rows of another model could show.
 */

/** The number of rows that the tab shows for each Forward. */
export const SENT_LIMIT = 50;

/** The integration types whose table the tab can read back. In phase 1, a Forward can write only
 *  to Postgres (the driver must write each value 1 time). */
const READ_BACK_TYPES = new Set(['postgres']);

export function canReadBack(i: Pick<Integration, 'type'>): boolean {
  return READ_BACK_TYPES.has(i.type);
}

/**
 * The paths that a Forward writes for 1 model (model-runner StateValues.ts, and subjects.FullPath
 * in the integrations service): the state of the root, the status, and the state of each other
 * machine. The machines come from the descriptor, so a stopped group has them too, and from the
 * runtime.
 */
export function statePaths(m: Pick<ModelView, 'id' | 'children' | 'machines'>): string[] {
  const base = `models/${m.id}`;
  const machines = new Set<string>();
  for (const c of m.children) {
    if (c.count) for (let i = 0; i < c.count; i++) machines.add(`${c.name}/${i}`);
    else machines.add(c.name);
  }
  for (const mc of m.machines) if (mc.path) machines.add(mc.path);
  return [`${base}/state`, `${base}/status`, ...[...machines].map((p) => `${base}/machines/${p}/state`)];
}

/**
 * The read of the last values that a Forward wrote to `table` for the paths: newest first. The
 * table name is 1 quoted identifier, as the driver quotes it when it writes (quoteDouble), so the
 * read names the same table. The paths are arguments, never text in the statement.
 */
export function sentQuery(table: string, paths: string[], limit = SENT_LIMIT): { statement: string; args: string[] } {
  const marks = paths.map((_, i) => `$${i + 1}`).join(', ');
  return {
    statement:
      `SELECT symbol_path, ts, num_val, str_val, bool_val, received_at FROM "${table.replaceAll('"', '""')}"` +
      ` WHERE symbol_path IN (${marks}) ORDER BY ts DESC, symbol_path LIMIT ${limit}`,
    args: paths,
  };
}

/** 1 value that a Forward wrote, as the table has it. */
export interface SentRow {
  /** The full path, unique with `valueNs`. */
  path: string;
  /** 'root' and 'status' are the model's own. 'machine' is a machine of a group. */
  kind: 'root' | 'status' | 'machine';
  /** The machine path for a machine of a group ('sensors/3'), else ''. */
  machine: string;
  value: string;
  /** The time of the value, from the gateway, in ns: exact for the key. */
  valueNs: bigint;
  valueMs: number;
  /** The time that the table got the row (received_at), in ms. NaN when the table did not say. */
  tableMs: number;
}

type Cell = string | number | boolean | null | undefined;

/** The rows of a sentQuery read. A row that has no path or no time is left out. */
export function toSentRows(modelId: string, rows: Array<Record<string, Cell>>): SentRow[] {
  const base = `models/${modelId}/`;
  const out: SentRow[] = [];
  for (const r of rows) {
    const path = typeof r.symbol_path === 'string' ? r.symbol_path : '';
    const valueNs = toNs(r.ts);
    if (!path.startsWith(base) || valueNs === undefined) continue;
    const rest = path.slice(base.length);
    const machine = rest.startsWith('machines/') && rest.endsWith('/state') ? rest.slice('machines/'.length, -'/state'.length) : '';
    out.push({
      path,
      kind: rest === 'status' ? 'status' : machine ? 'machine' : 'root',
      machine,
      value: valueText(r),
      valueNs,
      valueMs: Number(valueNs / 1_000_000n),
      tableMs: typeof r.received_at === 'string' ? Date.parse(r.received_at) : NaN,
    });
  }
  return out;
}

/** The ts column: an int64 arrives as a number when it is small, else as its exact decimal text. */
function toNs(ts: Cell): bigint | undefined {
  if (typeof ts === 'number' && Number.isInteger(ts)) return BigInt(ts);
  if (typeof ts === 'string' && /^-?\d+$/.test(ts)) return BigInt(ts);
  return undefined;
}

/** The value of a row: the 1 value column that is not null. A state is a str_val. */
function valueText(r: Record<string, Cell>): string {
  for (const k of ['str_val', 'num_val', 'bool_val']) {
    const v = r[k];
    if (v !== null && v !== undefined) return String(v);
  }
  return '';
}

/** The time from the value to the table, for a person: "120 ms", "1.2 s", "3 min". '' when it is
 *  not known, or negative (the 2 clocks differ). */
export function fmtDelay(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  return `${Math.round(ms / 3_600_000)} h`;
}
